import '../Sass/App.scss';
import React from 'react';
import { ReadmePage } from './ReadmePage';

const RULE_SECTIONS = [
    {
        title: 'game_overview',
        blocks: [
            'A rogue AI has unleashed a series of davastating computer viruses. Once-powerful governments are paying ransoms. Power grids are failing. Massive tracts of the Internet have collapsed. But a small band of developers is fighting back.',
            'The objective of Malware is simple: destroy the viruses before they can infect every computer in the world.',
            'Malware is best with two to four players (called "developers"), but can be adapted for solo play or for 5+ players. Players draw cards from their respective decks (called "Codebases") and use them to construct code that can damage viruses, disinfect computers, or otherwise effect gameplay.'
        ]
    },
    {
        title: 'requirements',
        blocks: [
            'To play Malware, you will need:',
            {
                type: 'list',
                items: [
                    'A Bootkit, which includes the game boards and base decks necessary to get started, and',
                    'At least one Codebase per player'
                ]
            },
            'You can purchase Bootkits and Codebases in our online store. Each Codebase has its own theme and unique gameplay mechanics. To get the most out of Malware, rotate Codebases between players and try out new combinations.',
            {
                type: 'code',
                // tone: 'info',
                // label: 'recommendation',
                text: 'After you purchase a Codebase, it will appear in the "entitlements" tab within your profile. You may playtest it online on using our playtest engine -- even if you haven\'t yet received your physical cards yet.'
            },
            {
                type: 'actions',
                actions: [
                    { label: 'shop bootkits', page: 'shop', sendId: 'shop bootkits' },
                    { label: 'shop codebases', page: 'shop', sendId: 'shop codebases' }
                ]
            }
        ]
    },
    {
        title: 'preparing_your_codebases',
        blocks: [
            'Each developer should select a Codebase, then search it for any program cards and set those cards face-up in front of them. Most Codebases have only one program.',
            'After programs have been removed, each developer should shuffle their Codebases and draw five (5) cards to their hand.',
            'Note that some Codebases have unique cards or mechanics that alter set-up. Start typing the name of your Codebase to locate its documentation on this website, which will include additional instructions and clarifications.',
            {
                type: 'actions',
                actions: [
                    { label: 'Codebase documentation', page: 'documentation', sendId: '' },
                ]
            }
        ]
    },
    {
        title: 'setting_the_table',
        blocks: [
            {
                type: 'callout',
                tone: 'step',
                label: 'configure variable tracker',
                text: 'Place the Variable Tracker somewhere within reach. Set its values to x = 1, y = 5, z = 1, %_infected_computers = 20.'
            },
            {
                type: 'callout',
                tone: 'step',
                label: 'viral functions & viral listeners',
                text: 'Shuffle the Viral Functions and the Viral Listener mini-decks. Please them beside each other near the center of the table.'
            },
            {
                type: 'callout',
                tone: 'step',
                label: 'machine learning',
                text: 'Separate the Machine Learning cards into three decks (x = 1, x = 2, and x = 3) based on their color/design. Place all three beside each other near the center of the table.'
            },
            {
                type: 'callout',
                tone: 'step',
                label: 'prepare viruses',
                text: 'Shuffle the virus trackers and draw a number of them equal to the number of developers in your game. Follow any instructions printed on them to add viral functions and viral listeners. Set the integrity of each virus (numbered bar along the top) to its maximum value.'
            },
            'After completing these steps, your tabletop should look something like this:',
            {
                type: 'image',
                src: 'https://placehold.co/300x100',
                alt: 'Malware gameplay setup',
                label: 'figure 01',
                caption: 'A sample Malware board state during virus escalation.'
            }
        ]
    },
    {
        title: 'gameplay_sequence',
        blocks: [
            'Malware is played in rounds, each of which has two phases:',
            {
                type: 'callout',
                tone: 'step',
                label: 'developer phase',
                text: 'In this phase, each developer takes a turn. During their turn, a developer plays two cards from their hand, then draws two cards from their Codebase.'
            },
            {
                type: 'callout',
                tone: 'step',
                label: 'machine learning phase',
                text: 'After all the developers have taken a turn, draw a card from the Machine Learning deck that corresponds with the current value of x per the Variable Tracker. For example, if x is set to 1, then draw from the x = 1 deck. Follow any instructions printed on the card.'
            }
        ]
    },
    {
        title: 'developer_phase',
        blocks: [
            'The developer closest to their computer goes first. Developer turns then proceed clockwise.',
            'Each developer may play up to two cards from their hand. There are five types of cards that can be found in Codebases:',
            {
                type: 'callout',
                tone: 'info',
                label: 'program',
                text: 'Most Codebases have just one of these, and it is almost always put into play druing set-up. Read its DEFAULT.INI section carefully, as it may specify if and when it runs. When it runs, any function cards on it run as well.'
            },
            {
                type: 'callout',
                tone: 'info',
                label: 'function',
                text: 'Play these onto your program by placing them face-up beside it. When your program runs, its functions run in the order in which they were played. Functions remain on your program until they are destroyed or forcibly discarded.'
            },
            {
                type: 'callout',
                tone: 'info',
                label: 'listener',
                text: 'Like functions, listeners are played face-up beside your program, and remain there until they are destroyed or discarded. They cause your program to run when some event has occurred. Each listener can be triggered once per round.'
            },
            {
                type: 'callout',
                tone: 'info',
                label: 'procedure',
                text: 'Procedures typically have an immediate, one-time effect. They are discarded after they are played.'
            },
            {
                type: 'callout',
                tone: 'info',
                label: 'special',
                text: 'Not every Codebase has special cards. Those that do have their own unique mechanic that dictates the way these cards work. Read the cards carefully, and consult Codebase documentation for additional rules and clarifications.'
            },
            'Remember that one developer\'s actions may cause viruses or other developers\' programs to run. Pay close attention to the run conditions and listeners on each program and virus.',
            'After playing cards, a developer should end their turn by drawing two cards from their Codebase. Developers may always draw, regardless of how many cards they have in their hand.',
            'If no cards remain in a developer\'s Codebase, that developer must finish the game using only the cards in their hand and on active programs. Developers should never shuffle their discard pile into their Codebase unless a card explicitly instructs them to do so.'
        ]
    },
    {
        title: 'machine_learning_phase',
        blocks: [
            'After all developers have completed their turns, draw a card from the Machine Learning deck corresponding with the current value of x. Carry out its instructions.',
            'As in the developer phase, pay close attention to all listeners in play. Many viruses run directly after the Machine Learning phase; some may be triggered by the effects of other viruses running.',
            {
                type: 'image',
                src: 'https://placehold.co/300x100',
                alt: 'Virus tracker layout',
                label: 'figure 02',
                caption: 'Understanding the various elements of a virus.'
            }
        ]
    },
    {
        title: 'variable_changes',
        blocks: [
            'The values of x, y and z cannot be set lower or higher than the variable\'s limits on the Variable Tracker (e.g. z cannot fall below 1, or exceed 5). However, some cards may temporarily change variable values (e.g. "for the rest of your turn, treat z as 2z"). The aforementioned limitation does not apply to these effects.',
            // {
            //     type: 'callout',
            //     tone: 'warning',
            //     label: 'hint',
            //     text: 'For the most part, increasing z helps developers, while increasing y and/or x helps viruses.'
            // },
            {
                type: 'code',
                text: 'For the most part, increasing z helps developers, while increasing y and/or x helps viruses.'
            },
        ]
    },
    {
        title: 'winning_and_losing',
        blocks: [
            'The developers win when no viruses remain in play.',
            'The developers lose when 100% of he world\'s computers have been infected (per the Variable Tracker).',
            {
                type: 'callout',
                tone: 'warning',
                label: 'scoring your win',
                text: 'Optionally, you can determine the quality of a win by the %_infected_computer variable. The lower, the better.'
            }
        ]
    },
    {
        title: 'helpful_clarifications',
        blocks: [
            'Below are a few additional tips and clarifications that new developers may find especially helpful',
            {
                type: 'list',
                items: [
                    'Remember that listeners can trigger only once per game round. A listener that triggers when y changes, for example, does not trigger twice in one round just because y increased twice in that round; it triggers only the first time the event occures within each round.',
                    'When multiple listeners fire simultaneously, developers may decide the sequence. Likewise, when more than one virus meets the criteria for a Machine Learning card (like decoyProtocol()), developers decide which gets targeted. And so on, for any scenarios involving a tie.',
                    'Developers may show their cards to each other and plan aloud. However, they may not swap or trade cards unless a card explicitly empowers them to do so.',
                    'Each developer must use a unique Codebase. Two copies of the same Codebase should not be used in the same game.',
                    'When a viral deck (Viral Functions, Viral Listeners or any of the Machine Learning decks) is depleted, you may shuffle its discard pile to replace/replenish it. The rule against shuffling discard piles applies only to developer Codebases.'
                ]
            }
        ]
    },
];

export function Rules(props) {
    return (
        <ReadmePage
            fileName="README.rules.md"
            title="Malware Game Rules"
            subtitle="This guide covers how to play Malware. A printed version of these instructions is included with any Bootkit purchase."
            version="0.1.0"
            status="draft"
            sections={RULE_SECTIONS}
            changePage={props.changePage}
        />
    );
}
